import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid, List, BookOpen, FileText, BookMarked, StickyNote,
  Download, Bookmark, BookmarkCheck, Eye, Calendar, User, Tag, Filter, X,
  ExternalLink, ChevronLeft, ChevronRight, Upload, ArrowLeft, ZoomIn, ZoomOut,
  Maximize, Minimize, RotateCw, MoreVertical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger
} from '@/components/ui/popover';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { SidebarProvider } from '@/components/ui/sidebar';
import { useSearchParams } from 'react-router-dom';
import AdminSidebar from './Sidebar';
import Header from './Header';
import Teachersidebar from '@/Teacherdashboard/Teachersidebar';
import Teacherheader from '@/Teacherdashboard/Teacherheader';
import { useToast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Document, Page, pdfjs } from 'react-pdf';
import mammoth from 'mammoth';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './pdf-sandbox-mobile.css';
import { db, storage } from '@/firebase/config';
import { collection, onSnapshot, addDoc, doc, getDocs, query as fbQuery, where, limit, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Define typeIcons within the file
const typeIcons = {
  book: BookOpen,
  paper: FileText,
  guide: BookMarked,
  notes: StickyNote,
};

// Types
interface Resource {
  id: string;
  title: string;
  subject: string;
  grade: string;
  examType: string;
  type: string;
  description: string;
  tags: string[];
  author?: string;
  isBookmarked: boolean;
  downloadCount: number;
  fileType: string;
  fileSize: string;
  uploadDate: string;
  pdfUrl: string;
}

interface FilterOptions {
  subject: string[];
  grade: string[];
  examType: string[];
  type: string[];
}

const resourceCategoriesConfig = [
  { id: 'books', name: 'Books', icon: 'BookOpen', type: 'book' },
  { id: 'papers', name: 'Past Papers', icon: 'FileText', type: 'paper' },
  { id: 'guides', name: 'Revision Guides', icon: 'BookMarked', type: 'guide' },
  { id: 'notes', name: 'Notes', icon: 'StickyNote', type: 'notes' },
  { id: 'bookmarks', name: 'Bookmarks', icon: 'Star', type: 'bookmarks' },
];

const subjects = ['Math', 'Physics', 'Chemistry', 'Biology', 'English', 'History', 'Geography', 'Computer Science'];
const grades = ['Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];
const examTypes = ['GCSE', 'IGCSE', 'A-Level', 'IB', 'Cambridge'];

const iconMap: { [key: string]: any } = {
  BookOpen,
  FileText,
  BookMarked,
  StickyNote,
};

const resourceTypes = [
  { value: 'book', label: 'Book' },
  { value: 'paper', label: 'Past Paper' },
  { value: 'guide', label: 'Study Guide' },
  { value: 'notes', label: 'Notes' },
];

// BookUploadForm Component
interface BookUploadFormProps {
  onClose?: () => void;
  onSubmit?: (data: Resource) => Promise<void>;
  schoolID: string;
}

function BookUploadForm({ onClose, onSubmit }: BookUploadFormProps) {
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    grade: '',
    examType: '',
    type: '',
    description: '',
    author: '',
    year: new Date().getFullYear().toString(),
    tags: [] as string[],
  });
  const [tagInput, setTagInput] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;
    setIsUploading(true);

    try {
      const storageRef = ref(storage, `global_resources/${Date.now()}_${selectedFile.name}`);
      await uploadBytes(storageRef, selectedFile);
      const fileUrl = await getDownloadURL(storageRef);

      const resourceData: Resource = {
        id: '',
        title: formData.title,
        subject: formData.subject,
        grade: formData.grade,
        examType: formData.examType,
        type: formData.type,
        description: formData.description,
        tags: formData.tags,
        author: formData.author || undefined,
        isBookmarked: false,
        downloadCount: 0,
        fileType: selectedFile.type.split('/')[1]?.toUpperCase() || 'PDF',
        fileSize: `${(selectedFile.size / 1024 / 1024).toFixed(2)}MB`,
        uploadDate: new Date().toISOString().split('T')[0],
        pdfUrl: fileUrl,
      };

      const docRef = await addDoc(collection(db, 'global_resources'), resourceData);
      resourceData.id = docRef.id;

      await onSubmit?.(resourceData);
      setIsUploading(false);
      onClose?.();
    } catch (err) {
      console.error('Error uploading resource:', err);
      toast({
        title: 'Upload Failed',
        description: 'Failed to upload resource. Please try again.',
        // destructive inline style
        style: {
          background: '#FEF2F2',
          color: '#7F1D1D',
          border: '1px solid #FECACA',
          padding: '10px',
          borderRadius: '8px',
        },
        duration: 5000,
      });
      setIsUploading(false);
    }
  };

  return (
    <ScrollArea className="h-[70vh] pr-4">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter book title"
              required
            />
          </div>
          <div>
            <Label htmlFor="subject">Subject *</Label>
            <Select value={formData.subject} onValueChange={(value) => handleInputChange('subject', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select subject" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                {subjects.map((subject) => (
                  <SelectItem key={subject} value={subject}>
                    {subject}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="grade">Grade *</Label>
            <Select value={formData.grade} onValueChange={(value) => handleInputChange('grade', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                {grades.map((grade) => (
                  <SelectItem key={grade} value={grade}>
                    {grade}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="examType">Exam Type</Label>
            <Select value={formData.examType} onValueChange={(value) => handleInputChange('examType', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select exam type" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                {examTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="type">Resource Type *</Label>
            <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                {resourceTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="author">Author</Label>
            <Input
              id="author"
              value={formData.author}
              onChange={(e) => handleInputChange('author', e.target.value)}
              placeholder="Enter author name"
            />
          </div>
          <div>
            <Label htmlFor="year">Year</Label>
            <Input
              id="year"
              type="number"
              value={formData.year}
              onChange={(e) => handleInputChange('year', e.target.value)}
              placeholder="Enter publication year"
              min="1900"
              max={new Date().getFullYear() + 10}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter a brief description of the resource"
              rows={3}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="tags">Tags</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add a tag"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              />
              <Button type="button" onClick={handleAddTag} variant="outline">
                Add
              </Button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className="flex items-center gap-1 bg-[#22C55E]/10 dark:bg-[#22C55E]/20 text-[#15803d] dark:text-[#4ADE80] border-[#22C55E]/20 hover:bg-[#22C55E]/20"
                  >
                    {tag}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => handleRemoveTag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="file">Upload File *</Label>
            <Card className="border-2 border-dashed border-[#E2E8F0]/50 dark:border-[#1E293B]/50 hover:border-[#E2E8F0]/75 dark:hover:border-[#1E293B]/75 transition-colors">
              <CardContent className="p-6">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-500 dark:text-gray-400 mb-4" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Choose a file to upload</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Supported formats: PDF, Word (.doc, .docx), PowerPoint (.ppt, .pptx), Text files
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Maximum file size: 50MB
                    </p>
                  </div>
                  <input
                    type="file"
                    id="file"
                    accept=".pdf,.doc,.docx,.txt,.ppt,.pptx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-4 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
                    onClick={() => document.getElementById('file')?.click()}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Select File
                  </Button>
                  {selectedFile && (
                    <div className="mt-4 p-3 bg-gray-100/50 dark:bg-[#1E293B]/50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          <span className="text-sm font-medium">{selectedFile.name}</span>
                          <Badge variant="outline" className="text-xs border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </Badge>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedFile(null)}
                          className="text-gray-700 dark:text-gray-300 hover:text-[#2563EB]"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <div className="space-y-4">
          {/* Form validation messages */}
          {(!formData.title || !formData.subject || !formData.grade || !formData.type || !selectedFile) && (
            <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md">
              Please complete the following required fields:
              <ul className="list-disc list-inside mt-1">
                {!formData.title && <li>Title</li>}
                {!formData.subject && <li>Subject</li>}
                {!formData.grade && <li>Grade</li>}
                {!formData.type && <li>Resource Type</li>}
                {!selectedFile && <li>File Upload</li>}
              </ul>
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4 border-t border-[#E2E8F0] dark:border-[#1E293B]">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!formData.title || !formData.subject || !formData.grade || !formData.type || !selectedFile || isUploading}
              className={cn(
                "transition-all duration-200",
                isUploading ? "bg-gray-400" : "bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]",
                (!formData.title || !formData.subject || !formData.grade || !formData.type || !selectedFile) 
                  ? "opacity-50 cursor-not-allowed" 
                  : "opacity-100"
              )}
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Uploading...
                </>
              ) : (
                'Upload Resource'
              )}
            </Button>
          </div>
        </div>
      </form>
    </ScrollArea>
  );
}

// ResourceCard Component
interface ResourceCardProps {
  resource: Resource;
  onBookmark: (id: string) => void;
  onDownload: (resource: Resource) => void;
  onView: (resource: Resource) => void;
  viewMode?: 'grid' | 'list';
  isDownloading?: boolean;
}

function ResourceCard({ resource, onBookmark, onDownload, onView, viewMode = 'grid', isDownloading = false }: ResourceCardProps) {
  const TypeIcon = typeIcons[resource.type as keyof typeof typeIcons] || FileText;

  const handleBookmark = () => {
    onBookmark(resource.id);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'book':
        return 'bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20';
      case 'paper':
        return 'bg-[#6B7280]/10 text-[#6B7280] border-[#6B7280]/20';
      case 'guide':
        return 'bg-[#22C55E]/10 text-[#22C55E] border-[#22C55E]/20';
      case 'notes':
        return 'bg-[#6B7280]/10 text-[#6B7280] border-[#6B7280]/20';
      default:
        return 'bg-[#6B7280]/10 text-[#6B7280] border-[#6B7280]/20';
    }
  };

  if (viewMode === 'list') {
    return (
      <Card className="resource-card group border shadow-sm hover:shadow-md flex flex-row p-4 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] rounded-xl">
        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className={cn('p-2 rounded-md border', getTypeColor(resource.type))}>
                <TypeIcon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-base mb-1 group-hover:text-[#2563EB] transition-colors">
                  {resource.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                  {resource.description}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBookmark}
              className="p-2 h-10 w-10 hover:bg-gray-50 dark:hover:bg-[#131E31]"
              aria-label={resource.isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            >
              {resource.isBookmarked ? (
                <BookmarkCheck className="h-5 w-5 text-[#22C55E]" />
              ) : (
                <Bookmark className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              )}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <Badge variant="outline" className="text-xs font-medium bg-[#22C55E]/10 dark:bg-[#22C55E]/20 text-[#15803d] dark:text-[#4ADE80] border-[#22C55E]/20 hover:bg-[#22C55E]/20">
              {resource.subject}
            </Badge>
            <span className="text-gray-500 dark:text-gray-400">Grade: {resource.grade}</span>
            <span className="text-gray-500 dark:text-gray-400">{resource.examType}</span>
            <div className="flex items-center space-x-1 text-gray-500 dark:text-gray-400">
              <Download className="h-4 w-4" />
              <span>{resource.downloadCount}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView(resource)}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
              aria-label={`View ${resource.title}`}
            >
              <Eye className="h-4 w-4 mr-2" />
              {resource.type === 'book' ? 'Read' : resource.type === 'paper' ? 'View Paper' : 'View'}
            </Button>
            <Button
              size="sm"
              onClick={() => onDownload(resource)}
              className="bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]"
              aria-label={`Download ${resource.title}`}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {isDownloading ? 'Downloading...' : 'Download'}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="resource-card group border shadow-sm hover:shadow-md min-h-[380px] flex flex-col bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] rounded-xl">
      <CardContent className="p-3 flex-1">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className={cn('p-1.5 rounded-md border', getTypeColor(resource.type))}>
              <TypeIcon className="h-4 w-4" />
            </div>
            <Badge variant="outline" className="text-xs capitalize border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
              {resource.type}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBookmark}
            className="p-1 h-8 w-8 hover:bg-gray-50 dark:hover:bg-[#131E31]"
            aria-label={resource.isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            {resource.isBookmarked ? (
              <BookmarkCheck className="h-4 w-4 text-[#22C55E]" />
            ) : (
              <Bookmark className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            )}
          </Button>
        </div>
        <h3 className="font-semibold text-sm mb-2 line-clamp-2 group-hover:text-[#2563EB] transition-colors">
          {resource.title}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
          {resource.description}
        </p>
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 dark:text-gray-400">Subject:</span>
            <Badge variant="outline" className="text-xs font-medium bg-[#22C55E]/10 dark:bg-[#22C55E]/20 text-[#15803d] dark:text-[#4ADE80] border-[#22C55E]/20 hover:bg-[#22C55E]/20 max-w-[120px] truncate">
              {resource.subject}
            </Badge>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 dark:text-gray-400">Grade:</span>
            <span className="font-medium truncate">{resource.grade}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 dark:text-gray-400">Exam:</span>
            <span className="font-medium truncate max-w-[120px]">{resource.examType}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3">
          <div className="flex items-center space-x-1">
            <Download className="h-3 w-3" />
            <span className="truncate">{resource.downloadCount}</span>
          </div>
          <div className="flex items-center space-x-1">
            <FileText className="h-3 w-3" />
            <span className="truncate">{resource.fileSize}</span>
          </div>
          <div className="flex items-center space-x-1 col-span-2">
            <Calendar className="h-3 w-3" />
            <span className="truncate">{new Date(resource.uploadDate).toLocaleDateString()}</span>
          </div>
        </div>
        {resource.author && (
          <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400 mb-3">
            <User className="h-3 w-3" />
            <span className="truncate">{resource.author}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-1">
          {resource.tags.slice(0, 2).map((tag) => (
            <Badge
              key={tag}
              variant="outline"
              className="text-xs px-1.5 py-0 border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
            >
              <Tag className="h-2 w-2 mr-1" />
              <span className="truncate max-w-[60px]">{tag}</span>
            </Badge>
          ))}
          {resource.tags.length > 2 && (
            <Badge variant="outline" className="text-xs px-1.5 py-0 border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
              +{resource.tags.length - 2}
            </Badge>
          )}
        </div>
      </CardContent>
      <div className="p-3 pt-0 flex flex-col gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onView(resource)}
          className="w-full bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
          aria-label={`View ${resource.title}`}
        >
          <Eye className="h-4 w-4 mr-2" />
          <span className="truncate">
            {resource.type === 'book' ? 'Read' : resource.type === 'paper' ? 'View Paper' : 'View'}
          </span>
        </Button>
        <Button
          size="sm"
          onClick={() => onDownload(resource)}
          className="w-full bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]"
          aria-label={`Download ${resource.title}`}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {isDownloading ? 'Downloading...' : 'Download'}
        </Button>
      </div>
    </Card>
  );
}

// ResourceFilters Component
interface ResourceFiltersProps {
  filters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  activeFiltersCount: number;
}

function ResourceFilters({ filters, onFilterChange, activeFiltersCount }: ResourceFiltersProps) {
  const updateFilter = (key: keyof FilterOptions, value: string) => {
    const currentValues = filters[key];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    onFilterChange({
      ...filters,
      [key]: newValues,
    });
  };

  const clearFilters = () => {
    onFilterChange({
      subject: [],
      grade: [],
      examType: [],
      type: [],
    });
  };

  const removeFilter = (key: keyof FilterOptions, value: string) => {
    onFilterChange({
      ...filters,
      [key]: filters[key].filter((v) => v !== value),
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between sm:justify-start sm:space-x-4">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
              aria-label="Open filters"
            >
              <Filter className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Filters</span>
              {activeFiltersCount > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-2 h-5 w-5 p-0 text-xs bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white dark:text-green-900"
                >
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 sm:w-96 p-4 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" align="start" side="bottom">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Filters</h4>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-gray-700 dark:text-gray-300 hover:text-[#2563EB]"
                    aria-label="Clear all filters"
                  >
                    Clear all
                  </Button>
                )}
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Subject</label>
                <Select onValueChange={(value) => updateFilter('subject', value)}>
                  <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    {subjects.map((subject) => (
                      <SelectItem
                        key={subject}
                        value={subject}
                        className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                      >
                        {subject}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Grade</label>
                <Select onValueChange={(value) => updateFilter('grade', value)}>
                  <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    {grades.map((grade) => (
                      <SelectItem
                        key={grade}
                        value={grade}
                        className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                      >
                        {grade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Exam Type</label>
                <Select onValueChange={(value) => updateFilter('examType', value)}>
                  <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                    <SelectValue placeholder="Select exam type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    {examTypes.map((type) => (
                      <SelectItem
                        key={type}
                        value={type}
                        className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                      >
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Resource Type</label>
                <Select onValueChange={(value) => updateFilter('type', value)}>
                  <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    <SelectItem
                      value="book"
                      className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                    >
                      Books
                    </SelectItem>
                    <SelectItem
                      value="paper"
                      className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                    >
                      Past Papers
                    </SelectItem>
                    <SelectItem
                      value="guide"
                      className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                    >
                      Revision Guides
                    </SelectItem>
                    <SelectItem
                      value="notes"
                      className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                    >
                      Notes
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        {activeFiltersCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={clearFilters}
            className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
            aria-label="Clear all filters"
          >
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
        )}
      </div>
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {filters.subject.map((subject) => (
            <Badge
              key={`subject-${subject}`}
              variant="secondary"
              className="pr-1 bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white"
            >
              Subject: {subject}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => removeFilter('subject', subject)}
                aria-label={`Remove ${subject} filter`}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
          {filters.grade.map((grade) => (
            <Badge
              key={`grade-${grade}`}
              variant="secondary"
              className="pr-1 bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white"
            >
              Grade: {grade}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => removeFilter('grade', grade)}
                aria-label={`Remove ${grade} filter`}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
          {filters.examType.map((type) => (
            <Badge
              key={`exam-${type}`}
              variant="secondary"
              className="pr-1 bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white"
            >
              Exam: {type}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => removeFilter('examType', type)}
                aria-label={`Remove ${type} filter`}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
          {filters.type.map((type) => (
            <Badge
              key={`type-${type}`}
              variant="secondary"
              className="pr-1 bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white"
            >
              Type: {type}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => removeFilter('type', type)}
                aria-label={`Remove ${type} filter`}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ResourcePreview Component
interface ResourcePreviewProps {
  resource: Resource | null;
  isOpen: boolean;
  onClose: () => void;
  onDownload: (resource: Resource) => void;
  onBookmark: (id: string) => void;
  onView: (resource: Resource) => void;
}

function ResourcePreview({ resource, isOpen, onClose, onDownload, onBookmark, onView }: ResourcePreviewProps) {
  if (!resource) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[80vh] p-0 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl">
        <DialogHeader className="p-6 pb-4 border-b border-[#F1F5F9] dark:border-[#1E293B]">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <DialogTitle className="text-xl font-semibold pr-8">{resource.title}</DialogTitle>
              <div className="flex items-center space-x-2">
                <Badge variant="outline" className="capitalize border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
                  {resource.type}
                </Badge>
                <Badge variant="secondary" className="bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white">
                  {resource.subject}
                </Badge>
                <Badge variant="outline" className="border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
                  {resource.grade}
                </Badge>
                <Badge variant="outline" className="border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
                  {resource.examType}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onBookmark(resource.id)}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
              aria-label={resource.isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            >
              {resource.isBookmarked ? (
                <BookmarkCheck className="h-4 w-4 mr-2" />
              ) : (
                <Bookmark className="h-4 w-4 mr-2" />
              )}
              {resource.isBookmarked ? 'Bookmarked' : 'Bookmark'}
            </Button>
            <Button
              size="sm"
              onClick={() => onDownload(resource)}
              className="bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]"
              aria-label={`Download ${resource.title}`}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView(resource)}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
              aria-label={`View ${resource.title}`}
            >
              <Eye className="h-4 w-4 mr-2" />
              {resource.type === 'book' ? 'Read' : resource.type === 'paper' ? 'View Paper' : 'View'}
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 p-6">
          <div className="h-full bg-gray-100/30 dark:bg-[#1E293B]/30 rounded-lg border-2 border-dashed border-gray-400/20 dark:border-gray-600/20 flex items-center justify-center">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-[#2563EB]/10 rounded-full flex items-center justify-center">
                <ExternalLink className="h-8 w-8 text-[#2563EB]" />
              </div>
              <div>
                <h3 className="font-medium text-lg mb-2">Resource Preview</h3>
                <p className="text-gray-500 dark:text-gray-400 max-w-md">
                  {resource.description}
                </p>
                <div className="mt-4 space-y-2 text-sm text-gray-500 dark:text-gray-400">
                  <p><strong>File Type:</strong> {resource.fileType}</p>
                  <p><strong>File Size:</strong> {resource.fileSize}</p>
                  <p><strong>Upload Date:</strong> {new Date(resource.uploadDate).toLocaleDateString()}</p>
                  {resource.author && <p><strong>Author:</strong> {resource.author}</p>}
                  <p><strong>Downloads:</strong> {resource.downloadCount}</p>
                </div>
              </div>
              <Button
                onClick={() => onDownload(resource)}
                className="mt-4 bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]"
                aria-label={`Download ${resource.title} to view`}
              >
                <Download className="h-4 w-4 mr-2" />
                Download to View
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// PDFReader Component
interface PDFReaderProps {
  pdfUrl: string;
  title: string;
  onBack: () => void;
  setIsFullscreen: (value: boolean) => void;
}

function PDFReader({ pdfUrl, title, onBack, setIsFullscreen }: PDFReaderProps) {
  const [zoom, setZoom] = useState(() => {
    // Set initial zoom based on screen size
    return window.matchMedia('(max-width: 768px)').matches ? 100 : 70;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [initialWidth, setInitialWidth] = useState<number>(Math.min(window.innerWidth * 0.8, 800));
  const [isFullscreen, setLocalIsFullscreen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [docContent, setDocContent] = useState<string>('');
  const [isWordDoc, setIsWordDoc] = useState(false);
  const [isPptx, setIsPptx] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const toggleFullscreen = () => {
    setLocalIsFullscreen((prev) => {
      const newState = !prev;
      setIsFullscreen(newState);
      return newState;
    });
  };

  useEffect(() => {
    const validateAndLoadDocument = async () => {
      setLoading(true);
      setError(null);

      try {
        const storageRef = ref(storage, pdfUrl);
        const validatedUrl = await getDownloadURL(storageRef);

        const ext = pdfUrl.split('.').pop()?.toLowerCase();
        const isDocLike = ext === 'doc' || ext === 'docx' || ext === 'htm' || ext === 'html';
        const isPptxFile = ext === 'ppt' || ext === 'pptx';
        setIsWordDoc(isDocLike);
        setIsPptx(isPptxFile);

        if (isDocLike) {
          await loadWordDocument(validatedUrl, ext);
        } else if (isPptxFile) {
          setDocContent(validatedUrl);
          setLoading(false);
        } else {
          setDocContent(validatedUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error validating URL:', err);
        setError('Failed to load the document. Please check the file URL or try again later.');
        setLoading(false);
        toast({
          title: 'Error',
          description: 'Unable to load the document. Please try again later.',
          style: {
            background: '#FEF2F2',
            color: '#7F1D1D',
            border: '1px solid #FECACA',
            padding: '10px',
            borderRadius: '8px',
          },
          duration: 5000,
        });
      }
    };

    validateAndLoadDocument();
  }, [pdfUrl, toast]);

  const loadWordDocument = async (url: string, ext?: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }
      if (ext === 'doc' || ext === 'htm' || ext === 'html') {
        const text = await response.text();
        if (text.trim().startsWith('<') || text.toLowerCase().includes('<html')) {
          setDocContent(text);
        } else {
          setDocContent('<p>Unable to render legacy .doc file. Please use .docx or PDF.</p>');
        }
        setTotalPages(1);
        setLoading(false);
        return;
      }
      const arrayBuffer = await response.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setDocContent(result.value);
      setTotalPages(1);
      setLoading(false);
    } catch (error) {
      console.error('Error loading document:', error);
      setDocContent('<p>Error loading document. Please try again.</p>');
      setLoading(false);
      toast({
        title: 'Error',
        description: 'Failed to load Word document. Please try again.',
        style: {
          background: '#FEF2F2',
          color: '#7F1D1D',
          border: '1px solid #FECACA',
          padding: '10px',
          borderRadius: '8px',
        },
        duration: 5000,
      });
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
    setError('Failed to load the PDF. Please ensure the file is valid and try again.');
    setLoading(false);
    toast({
      title: 'Error',
      description: 'Failed to load the PDF. Please try again.',
      style: {
        background: '#FEF2F2',
        color: '#7F1D1D',
        border: '1px solid #FECACA',
        padding: '10px',
        borderRadius: '8px',
      },
      duration: 5000,
    });
  };

  // calculate a responsive initial width for the PDF based on viewport
  useEffect(() => {
    const calculateWidth = () => {
      try {
        const screenWidth = window.innerWidth || 800;
        const maxWidth = 1000;
        const padding = screenWidth < 768 ? 32 : 64;
        setInitialWidth(Math.min(screenWidth - padding, maxWidth));
      } catch (e) {
        setInitialWidth(800);
      }
    };
    calculateWidth();
    window.addEventListener('resize', calculateWidth);
    return () => window.removeEventListener('resize', calculateWidth);
  }, []);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 10, 50));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleDownload = async () => {
    try {
      toast({
        title: 'Fetching file...',
        description: `Preparing "${title}" for download securely. Please wait.`,
        className: "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
        duration: 4000,
      });

      const storageRef = ref(storage, pdfUrl);
      const downloadUrl = await getDownloadURL(storageRef);
      
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      const cleanUrl = pdfUrl.split('?')[0];
      const fileExtension = cleanUrl.split('.').pop()?.toLowerCase() || 'pdf';
      link.download = `${title}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: 'Download Failed',
        description: 'Unable to download the file. Please try again later.',
        className: "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
        duration: 5000,
      });
    }
  };

  return (
    <div className={`flex flex-col h-full bg-gray-50 dark:bg-gray-900 ${isFullscreen ? 'fixed inset-0 z-50' : 'relative'}`}>
      <div className="border-b bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
    <div className="flex items-center justify-between p-2 sm:p-4 gap-2 sm:gap-4 max-w-full sticky top-0 z-10">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <Button
              variant="outline"
              onClick={onBack}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Back to resources"
            >
              <ArrowLeft className="h-4 w-4 mr-0 md:mr-2 text-gray-700 dark:text-gray-300" />
              <span className="hidden md:inline text-gray-700 dark:text-gray-300">Back</span>
            </Button>
            <h1 className="text-lg font-semibold truncate text-gray-900 dark:text-white">{title}</h1>
          </div>

          {/* Mobile: 3-dot menu for PDF controls (visible only on small screens) */}
          <div className="sm:hidden ml-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open PDF actions">
                  <MoreVertical className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <div className="flex flex-col gap-2">
                  <Button variant="ghost" size="sm" onClick={handlePrevPage} disabled={currentPage === 1} className="justify-start">
                    <ChevronLeft className="h-4 w-4 mr-2" /> Prev
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleNextPage} disabled={currentPage === totalPages} className="justify-start">
                    Next <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                  <div className="flex flex-col gap-1 px-1">
                    <Button variant="ghost" size="sm" onClick={handleZoomOut} className="justify-start">
                      <ZoomOut className="h-4 w-4 mr-2" />
                      Zoom Out
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleZoomIn} className="justify-start">
                      <ZoomIn className="h-4 w-4 mr-2" />
                      Zoom In
                    </Button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleRotate} className="justify-start">
                    <RotateCw className="h-4 w-4 mr-2" /> Rotate
                  </Button>
                  <Button variant="ghost" size="sm" onClick={toggleFullscreen} className="justify-start">
                    {isFullscreen ? <Minimize className="h-4 w-4 mr-2" /> : <Maximize className="h-4 w-4 mr-2" />}
                    {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleDownload} className="justify-start">
                    <Download className="h-4 w-4 mr-2" /> Download
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {!isWordDoc && totalPages > 0 && (
            <div className="hidden sm:flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap px-2">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomOut}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
              aria-label="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-700 dark:text-gray-300 min-w-[3rem] text-center">{zoom}%</span>
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomIn}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
              aria-label="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            {!isWordDoc && (
              <Button
                variant="outline"
                size="icon"
                onClick={handleRotate}
                className="hidden sm:inline-flex bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
                aria-label="Rotate document"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={toggleFullscreen}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <Minimize className="h-4 w-4 mr-2" /> : <Maximize className="h-4 w-4 mr-2" />}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleDownload}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
              aria-label="Download document"
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="px-4 pb-3 pt-1">
          <Slider
            value={[zoom]}
            onValueChange={(value) => setZoom(value[0])}
            min={50}
            max={200}
            step={10}
            className="w-full max-w-xs mx-auto"
            aria-label="Zoom level"
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900">
        <div className="h-full w-full overflow-auto"> 
          <div className="flex items-center justify-center min-h-full p-4">
            {loading ? (
              <div className="text-center">
                <p className="text-gray-500 dark:text-gray-400">Loading document...</p>
              </div>
            ) : error ? (
              <div className="text-center">
                <p className="text-destructive">{error}</p>
                <Button
                  variant="outline"
                  onClick={onBack}
                  className="mt-4 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
                  aria-label="Back to resources"
                >
                  Back to Resources
                </Button>
              </div>
            ) : isWordDoc ? (
              <Card
                className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] shadow-md max-w-4xl w-full mx-auto"
                style={{
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: 'top center',
                  transition: 'transform 0.2s ease-in-out',
                }}
              >
                <div className="p-8 prose prose-sm max-w-none text-gray-900 dark:text-white" dangerouslySetInnerHTML={{ __html: docContent }} />
              </Card>
            ) : isPptx ? (
              <div className="w-full h-full min-h-[600px]" style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center', transition: 'transform 0.2s ease-in-out' }}>
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(docContent)}`}
                  className="w-full h-full min-h-[600px] border-0 rounded-lg shadow-md"
                  title={title}
                  sandbox="allow-scripts allow-same-origin allow-popups"
                />
              </div>
            ) : (
              <div className="pdf-viewport overflow-auto w-full h-full">
                  <div className="pdf-container">
                  <div
                    className="pdf-surface rounded-lg shadow-md bg-white dark:bg-[#020817]"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: 'top center',
                    transition: 'transform 0.2s ease-in-out',
                  }}
                >
                  <Document
                    file={docContent}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={<div className="text-gray-500 dark:text-gray-400">Loading PDF...</div>}
                    error={<div className="text-destructive">Error loading PDF. Please try again.</div>}
                  >
                    <Page
                      pageNumber={currentPage}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      width={Math.max(300, Math.round(initialWidth * (zoom / 100)))}
                      className="max-w-full h-auto"
                    />
                  </Document>
                </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isWordDoc && totalPages > 1 && (
        <div className="lg:hidden border-t bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] p-3">
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="flex-1 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">
              {currentPage}/{totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="flex-1 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
              aria-label="Next page"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ResourceDashboard Component
interface ResourceDashboardProps {
  activeCategory?: string;
  searchQuery?: string;
  layoutType?: 'admin' | 'teacher';
}

export function ResourceDashboard({ activeCategory = 'overview', layoutType = 'admin' }: Omit<ResourceDashboardProps, 'searchQuery'>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearchQuery = searchParams.get('search') || '';
  const { toast } = useToast();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [resources, setResources] = useState<Resource[]>([]);
  const [activeTab, setActiveTab] = useState(activeCategory);
  const [filters, setFilters] = useState<FilterOptions>({
    subject: [],
    grade: [],
    examType: [],
    type: [],
  });
  const [previewResource, setPreviewResource] = useState<Resource | null>(null);
  const [viewResource, setViewResource] = useState<Resource | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'title' | 'uploadDate' | 'downloadCount'>('title');
  const [currentPage, setCurrentPage] = useState(1);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const resourcesPerPage = 12;
  const [searchQuery, setSearchQuery] = useState<string>(urlSearchQuery);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Resource[] | null>(null);
  const [resourceCategories, setResourceCategories] = useState(
    resourceCategoriesConfig.map(cat => ({ ...cat, count: 0 }))
  );
  const [userId, setUserId] = useState<string | null>(null);
  const [userBookmarks, setUserBookmarks] = useState<Set<string>>(new Set());
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Perform a Firestore-backed search. We'll run a few focused queries (title prefix, tags, subject, author)
  // and merge results. This is best-effort full-text behavior using Firestore primitives.
  const performFirebaseSearch = async (q: string) => {
    const term = q.trim();
    if (!term) {
      setSearchResults(null);
      return;
    }
    setIsSearching(true);
    try {
      const col = collection(db, 'global_resources');
      const promises: Promise<any>[] = [];

      // Title prefix search using range queries
      const titleQ = fbQuery(col, where('title', '>=', term), where('title', '<=', term + '\ \uffff'), limit(50));
      promises.push(getDocs(titleQ));

      // Subject exact match
      const subjectQ = fbQuery(col, where('subject', '==', term), limit(50));
      promises.push(getDocs(subjectQ));

      // Tags array-contains (if tags are exact match)
      const tagsQ = fbQuery(col, where('tags', 'array-contains', term), limit(50));
      promises.push(getDocs(tagsQ));

      // Author exact match
      const authorQ = fbQuery(col, where('author', '==', term), limit(50));
      promises.push(getDocs(authorQ));

      const snaps = await Promise.all(promises);
      const resultsMap: Record<string, Resource> = {};
      snaps.forEach((snap) => {
        snap.docs.forEach((d: any) => {
          const data = d.data();
          const res: Resource = {
            id: d.id,
            title: data.title || '',
            subject: data.subject || '',
            grade: data.grade || '',
            examType: data.examType || '',
            type: data.type || '',
            description: data.description || '',
            tags: data.tags || [],
            author: data.author || undefined,
            isBookmarked: !!data.isBookmarked,
            downloadCount: data.downloadCount || 0,
            fileType: data.fileType || '',
            fileSize: data.fileSize || '',
            uploadDate: data.uploadDate || new Date().toISOString(),
            pdfUrl: data.pdfUrl || '',
          };
          resultsMap[res.id] = res;
        });
      });

      // If no direct query results, as a fallback fetch a small batch and filter client-side (case-insensitive substring)
      let merged = Object.values(resultsMap);
      if (merged.length === 0) {
        const allSnap = await getDocs(fbQuery(col, limit(200)));
        const list = allSnap.docs.map((d: any) => ({ ...(d.data() as any), id: d.id } as Resource));
        const lower = term.toLowerCase();
        merged = list.filter((r) =>
          r.title.toLowerCase().includes(lower) ||
          r.subject.toLowerCase().includes(lower) ||
          r.description.toLowerCase().includes(lower) ||
          (r.author || '').toLowerCase().includes(lower) ||
          (r.tags || []).some((t) => t.toLowerCase().includes(lower))
        );
      }

      setSearchResults(merged);
    } catch (err) {
      console.error('Search error:', err);
      toast({
        title: 'Search Error',
        description: 'Failed to search resources. Try again.',
        style: {
          background: '#FEF2F2',
          color: '#7F1D1D',
          border: '1px solid #FECACA',
          padding: '10px',
          borderRadius: '8px',
        },
        duration: 5000,
      });
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        try {
          const idTokenResult = await user.getIdTokenResult();
          const schoolID = idTokenResult.claims.schoolID as string | undefined;
          if (schoolID) {
            setSchoolID(schoolID);
          } else {
            toast({
              title: 'Access Denied',
              description: 'No school affiliation found.',
              style: {
                background: '#FEF2F2',
                color: '#7F1D1D',
                border: '1px solid #FECACA',
                padding: '10px',
                borderRadius: '8px',
              },
              duration: 5000,
            });
            navigate('/dashboard');
          }
          
          // Fetch user's bookmarks
          try {
            const bookmarksRef = collection(db, 'users', user.uid, 'bookmarks');
            const bookmarksSnap = await getDocs(bookmarksRef);
            const bookmarkIds = new Set(bookmarksSnap.docs.map(d => d.id));
            setUserBookmarks(bookmarkIds);
          } catch (err) {
            console.error('Error fetching user bookmarks:', err);
          }
        } catch (error) {
          console.error('Error fetching user claims:', error);
          toast({
            title: 'Error',
            description: 'Failed to fetch user data. Please try again.',
            variant: "destructive",
            duration: 5000,
          });
          navigate('/dashboard');
        }
      } else {
        setUserId(null);
        setUserBookmarks(new Set());
        toast({
          title: 'Authentication Required',
          description: 'Please log in to access resources.',
          variant: "destructive",
          duration: 5000,
        });
        navigate('/login-page');
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, toast]);

    useEffect(() => {
    // schoolID is no longer needed – we read the global collection
    setIsLoading(true);
    const resourcesRef = collection(db, 'global_resources');

    const unsubscribe = onSnapshot(
      resourcesRef,
      (snapshot) => {
        const resourceList = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id,
          } as Resource;
        });
        setResources(resourceList);
        setIsLoading(false);
      },
      (error) => {
        console.error('Error fetching resources:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch resources. Please try again.',
          variant: "destructive",
          duration: 5000,
        });
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [toast]);   // <-- remove userBookmarks from deps

  useEffect(() => {
    const fetchCategoryCounts = async () => {
      try {
        const resourcesRef = collection(db, 'global_resources');
        const counts = await Promise.all(
          resourceCategoriesConfig.map(async (category) => {
            if (category.id === 'bookmarks') {
              return {
                ...category,
                count: userBookmarks.size
              };
            }

            const q = fbQuery(resourcesRef, where('type', '==', category.type));
            const snapshot = await getDocs(q);
            return {
              ...category,
              count: snapshot.size
            };
          })
        );
        setResourceCategories(counts);
      } catch (error) {
        console.error('Error fetching category counts:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch resource counts',
          variant: "destructive",
          duration: 5000,
        });
      }
    };

    fetchCategoryCounts();
  }, [toast, userBookmarks]);

  const filteredResources = useMemo(() => {
    // Merge user bookmarks into the source list (whether it's search results or all resources)
    const rawSource = searchResults ?? resources;
    const source = rawSource.map(r => ({
      ...r,
      isBookmarked: userBookmarks.has(r.id)
    }));
    
    let result = source.filter((resource) => {
      if (activeTab === 'bookmarks' && !resource.isBookmarked) return false;
      if (activeTab === 'recent') {
        const recentIds = ['1', '3', '5', '8'];
        if (!recentIds.includes(resource.id)) return false;
      }



      if (activeTab === 'trending') {
        const trendingIds = ['2', '4', '6', '7', '8'];
        if (!trendingIds.includes(resource.id)) return false;
      }
      if (['books', 'papers', 'guides', 'notes'].includes(activeTab)) {
        const categoryTypeMap: { [key: string]: string } = {
          books: 'book',
          papers: 'paper',
          guides: 'guide',
          notes: 'notes',
        };
        if (resource.type !== categoryTypeMap[activeTab]) return false;
      }
      const effectiveQuery = (searchQuery || urlSearchQuery || '').toLowerCase();
      if (effectiveQuery) {
        const query = effectiveQuery;
        const matchesSearch =
          resource.title.toLowerCase().includes(query) ||
          resource.subject.toLowerCase().includes(query) ||
          resource.description.toLowerCase().includes(query) ||
          resource.tags.some((tag) => tag.toLowerCase().includes(query)) ||
          (resource.author && resource.author.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }
      if (filters.subject.length > 0 && !filters.subject.includes(resource.subject)) return false;
      if (filters.grade.length > 0 && !filters.grade.includes(resource.grade)) return false;
      if (filters.examType.length > 0 && !filters.examType.includes(resource.examType)) return false;
      if (filters.type.length > 0 && !filters.type.includes(resource.type)) return false;
      return true;
    });

    result.sort((a, b) => {
      if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      } else if (sortBy === 'uploadDate') {
        return new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();
      } else if (sortBy === 'downloadCount') {
        return b.downloadCount - a.downloadCount;
      }
      return 0;
    });

    return result;
  }, [resources, activeTab, urlSearchQuery, filters, sortBy, searchResults, userBookmarks]);

  const activeFiltersCount = Object.values(filters).flat().length;

  const paginatedResources = filteredResources.slice(
    (currentPage - 1) * resourcesPerPage,
    currentPage * resourcesPerPage
  );

  const handleBookmark = async (id: string) => {
    if (!userId) {
      toast({
        title: 'Login Required',
        description: 'Please log in to bookmark resources.',
        variant: "destructive",
        duration: 4000,
      });
      return;
    }

    const resource = resources.find((r) => r.id === id);
    if (!resource) return;
    if (!id) {
      console.error("Invalid resource ID:", id);
      return;
    }

    const isCurrentlyBookmarked = userBookmarks.has(id);
    // Explicitly create collection ref first, then doc ref
    const bookmarksCollectionRef = collection(db, 'users', userId, 'bookmarks');
    const bookmarkRef = doc(bookmarksCollectionRef, id);

    try {
      if (isCurrentlyBookmarked) {
        // Remove bookmark
        await deleteDoc(bookmarkRef);
        setUserBookmarks(prev => {
          const updated = new Set(prev);
          updated.delete(id);
          return updated;
        });
      } else {
        // Add bookmark
        await setDoc(bookmarkRef, {
          resourceId: id,
          title: resource.title,
          bookmarkedAt: new Date().toISOString(),
        });
        setUserBookmarks(prev => {
          const updated = new Set(prev);
          updated.add(id);
          return updated;
        });
      }

      // Update local resources state
      setResources((prev) =>
        prev.map((r) => (r.id === id ? { ...r, isBookmarked: !isCurrentlyBookmarked } : r))
      );

      toast({
        title: !isCurrentlyBookmarked ? 'Bookmark Added' : 'Bookmark Removed',
        description: `"${resource.title}" ${!isCurrentlyBookmarked ? 'added to' : 'removed from'} your bookmarks.`,
        className: !isCurrentlyBookmarked 
          ? "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300"
          : "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
        duration: 4000,
      });
    } catch (error) {
      console.error('Error updating bookmark:', error);
      toast({
        title: 'Bookmark Error',
        description: 'Failed to update bookmark. Please try again.',
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleDownload = async (resource: Resource) => {
  setDownloadingId(resource.id);
  try {
    toast({
      title: 'Fetching file...',
      description: `Preparing "${resource.title}" for download securely. Please wait.`,
      className: "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
      duration: 4000,
    });

    const storageRef = ref(storage, resource.pdfUrl);
    const downloadUrl = await getDownloadURL(storageRef);
    
    const response = await fetch(downloadUrl);
    if (!response.ok) throw new Error('Network response was not ok');
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = blobUrl;
    const cleanUrl = resource.pdfUrl.split('?')[0];
    const fileExtension = cleanUrl.split('.').pop()?.toLowerCase() || 'pdf';
    link.download = `${resource.title}.${fileExtension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
  } catch (error) {
    console.error('Error downloading resource:', error);
    toast({
      title: 'Download Failed',
      description: 'Unable to download the resource. Please check the file URL or try again later.',
      className: "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
      duration: 5000,
    });
  } finally {
    setDownloadingId(null);
  }
};

  const handleView = (resource: Resource) => {
    setViewResource(resource);
  };

  const handleUploadSubmit = async (data: Resource) => {
    // Avoid appending the uploaded resource locally. The Firestore onSnapshot listener
    // will pick up the new document and update `resources`, which prevents a temporary
    // duplicate appearing immediately after upload.
    toast({
      title: 'Upload successful',
      description: `"${data.title}" has been added to the resources.`,
      // Inline styling to ensure the toast appears correctly regardless of global styles
      style: {
        background: '#ECFDF5',
        color: '#065F46',
        border: '1px solid #D1FAE5',
        padding: '10px',
        borderRadius: '8px',
      },
      duration: 4000,
    });
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 'overview':
        return 'Overview';
      case 'books':
        return 'Books';
      case 'papers':
        return 'Past Papers';
      case 'guides':
        return 'Revision Guides';
      case 'notes':
        return 'Notes';
      case 'bookmarks':
        return 'Bookmarked Resources';
      case 'recent':
        return 'Recently Accessed';
      case 'trending':
        return 'Trending Resources';
      default:
        return 'Resources';
    }
  };



  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading || !schoolID) {
    return (
      <SidebarProvider>
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
            .font-roboto {
              font-family: 'Roboto', sans-serif;
            }
          `}
        </style>
        <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
          {layoutType === 'teacher' ? <Teachersidebar /> : <AdminSidebar onSectionChange={() => {}} />}
          <div className="flex flex-col flex-1">
            {layoutType === 'teacher' ? <Teacherheader /> : <Header />}
            <main className="flex-grow p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2563EB]"></div>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .font-roboto {
            font-family: 'Roboto', sans-serif;
          }
          .pdf-sandbox {
            display: flex;
            justify-content: center;
            width: 100%;
            height: 100%;
          }
          .pdf-surface {
            background: white;
            max-width: 100%;
            overflow: hidden;
          }
          .fixed-sidebar {
            position: fixed;
            top: 0;
            left: 0;
            height: 100vh;
            z-index: 1000;
            ${isFullscreen ? 'display: none;' : ''}
          }
        `}
      </style>
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
          <div className="fixed-sidebar hidden md:block">
            {layoutType === 'teacher' ? <Teachersidebar /> : <AdminSidebar onSectionChange={() => {}} />}
          </div>
          <div className="flex flex-col flex-1 min-h-screen ml-0 md:ml-[250px] w-full">
            {!isFullscreen && (
              <div className="sticky top-0 z-40 w-full">
                {layoutType === 'teacher' ? <Teacherheader /> : <Header />}
              </div>
            )}
            {viewResource ? (
                            <PDFReader
                pdfUrl={viewResource.pdfUrl}
                title={viewResource.title}
                onBack={() => {
                  setViewResource(null);
                  setIsFullscreen(false); // Reset fullscreen state on back
                }}
                setIsFullscreen={setIsFullscreen}
              />
            ) : (
              <main className="flex-1 overflow-y-auto p-2 sm:p-4 md:p-6 lg:p-8 pt-3 sm:pt-4 md:pt-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
                <div className="flex-1 space-y-3 p-2 sm:p-4 md:p-6 max-w-7xl mx-auto">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                    <div className="flex items-center gap-2">
                      {activeTab !== 'overview' && (
                        <Button
                          variant="outline"
                          onClick={() => setActiveTab('overview')}
                          className="mr-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                          aria-label="Back to categories"
                        >
                          <ArrowLeft className="h-4 w-4 mr-0 md:mr-2" />
                          <span className="hidden md:inline">Back</span>
                        </Button>
                      )}
                      <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">{getTabTitle()}</h1>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative w-full sm:w-auto">
                        <Input
                          placeholder="Search resources..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { performFirebaseSearch(searchQuery); } }}
                          className="w-full sm:w-[200px] bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
                          aria-label="Search resources"
                        />
                        {isSearching && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">Searching…</div>
                        )}
                      </div>
                      <Button
                        variant={viewMode === 'grid' ? 'default' : 'outline'}
                        size="icon"
                        onClick={() => setViewMode('grid')}
                        className={viewMode === 'grid' ? 'bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]' : 'bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]'}
                        aria-label="Grid view"
                      >
                        <Grid className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={viewMode === 'list' ? 'default' : 'outline'}
                        size="icon"
                        onClick={() => setViewMode('list')}
                        className={viewMode === 'list' ? 'bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]' : 'bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]'}
                        aria-label="List view"
                      >
                        <List className="h-4 w-4" />
                      </Button>
                      <Select value={sortBy} onValueChange={(value) => setSortBy(value as 'title' | 'uploadDate' | 'downloadCount')}>
                        <SelectTrigger className="w-[140px] bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                          <SelectItem value="title" className="text-gray-700 dark:text-gray-300">Sort by Title</SelectItem>
                          <SelectItem value="uploadDate" className="text-gray-700 dark:text-gray-300">Sort by Date</SelectItem>
                          <SelectItem value="downloadCount" className="text-gray-700 dark:text-gray-300">Sort by Downloads</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={() => setIsUploadOpen(true)}
                        className="bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]"
                        aria-label="Upload resource"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload
                      </Button>
                    </div>
                  </div>

                  {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      {resourceCategories.map((category) => {
                        const Icon = iconMap[category.icon as keyof typeof iconMap] || FileText;
                        return (
                          <Card
                              key={category.id}
                              className="group cursor-pointer bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] hover:shadow-md transition-shadow"
                              onClick={() => {
                                // When a user clicks a category, clear any existing search state so
                                // the category view reflects only the collection data. This prevents
                                // searchResults from showing unrelated items when a category is empty.
                                setActiveTab(category.id);
                                setSearchResults(null);
                                setSearchQuery('');
                                setCurrentPage(1);
                                setPreviewResource(null);
                                setViewResource(null);
                                // Clear the 'search' URL param as well so urlSearchQuery becomes empty
                                // and the category doesn't inherit previous search filters.
                                try {
                                  setSearchParams({});
                                } catch (e) {
                                  // ignore if router doesn't allow
                                }
                              }}
                            >
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                              <CardTitle className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-[#2563EB]">
                                {category.name}
                              </CardTitle>
                              <Icon className="h-5 w-5 text-[#2563EB]" />
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">{category.count}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Resources available</p>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  {activeTab !== 'overview' && (
                    <div className="mb-6">
                      <ResourceFilters
                        filters={filters}
                        onFilterChange={setFilters}
                        activeFiltersCount={activeFiltersCount}
                      />
                    </div>
                  )}

                  {filteredResources.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="mx-auto h-12 w-12 text-gray-500 dark:text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">No resources found</h3>
                      <p className="text-gray-500 dark:text-gray-400">
                        Try adjusting your filters or search query.
                      </p>
                    </div>
                  ) : (
                    <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'space-y-4'}>
                      {paginatedResources.map((resource, idx) => (
                        <ResourceCard
                          key={resource.id || `resource-${idx}-${(resource.title || '').slice(0,20)}`}
                          resource={resource}
                          onBookmark={handleBookmark}
                          onDownload={handleDownload}
                          onView={handleView}
                          viewMode={viewMode}
                          isDownloading={downloadingId === resource.id}
                        />
                      ))}
                    </div>
                  )}

                  {filteredResources.length > resourcesPerPage && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.ceil(filteredResources.length / resourcesPerPage) }, (_, i) => i + 1)
                          .slice(
                            Math.max(0, currentPage - 3),
                            Math.min(Math.ceil(filteredResources.length / resourcesPerPage), currentPage + 2)
                          )
                          .map((page) => (
                            <Button
                              key={page}
                              variant={currentPage === page ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handlePageChange(page)}
                              className={
                                currentPage === page
                                  ? 'bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]'
                                  : 'bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]'
                              }
                              aria-label={`Page ${page}`}
                            >
                              {page}
                            </Button>
                          ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === Math.ceil(filteredResources.length / resourcesPerPage)}
                        className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
                  <DialogContent className="max-w-3xl bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-semibold">Upload New Resource</DialogTitle>
                    </DialogHeader>
                    <BookUploadForm
                      onClose={() => setIsUploadOpen(false)}
                      onSubmit={handleUploadSubmit}
                      schoolID={schoolID}
                    />
                  </DialogContent>
                </Dialog>

                <ResourcePreview
                  resource={previewResource}
                  isOpen={!!previewResource}
                  onClose={() => setPreviewResource(null)}
                  onDownload={handleDownload}
                  onBookmark={handleBookmark}
                  onView={handleView}
                />
              </main>
            )}
          </div>
        </div>
        <Toaster />
      </SidebarProvider>
    </>
  );
}